// src/services/firebase/auth.ts
import { 
  signInWithEmailAndPassword, 
  sendPasswordResetEmail,
  signOut as firebaseSignOut,
  UserCredential,
  User
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './config';
import logger from '../../utils/debugLogger';

// Types
export interface AuthResponse {
  success: boolean;
  user?: User;
  isAdmin?: boolean;
  error?: string;
}

// Check if a user UID has isAdmin = true in users collection
export const checkUserIsAdmin = async (uid: string): Promise<boolean> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (userDoc.exists()) {
      return userDoc.data()?.isAdmin === true;
    }
    return false;
  } catch (error) {
    logger.error('Error checking admin status:', error);
    return false;
  }
};

// Login with email and password
export const loginWithEmailPassword = async (
  email: string, 
  password: string
): Promise<AuthResponse> => {
  try {
    logger.info(`Attempting login for: ${email}`);
    
    // Attempt login with Firebase
    const userCredential: UserCredential = await signInWithEmailAndPassword(
      auth, 
      email, 
      password
    );
    
    const uid = userCredential.user.uid;
    logger.info(`Authentication successful for: ${email}, uid: ${uid}`);
    
    // 1. Check if user is an administrator in users collection
    const isAdmin = await checkUserIsAdmin(uid);
    if (isAdmin) {
      logger.info(`Admin user authenticated: ${email} (${uid})`);
      return {
        success: true,
        user: userCredential.user,
        isAdmin: true
      };
    }
    
    // 2. Check school record in schools collection
    logger.info(`Checking school record in seektrudb for uid: ${uid}`);
    const schoolDoc = await getDoc(doc(db, 'schools', uid));
    
    if (!schoolDoc.exists()) {
      logger.warn(`No school or admin record found for uid: ${uid}`);
      await firebaseSignOut(auth);
      return {
        success: false,
        error: 'This account is not registered as a school or administrator. Please use the mobile app for parent accounts.'
      };
    }
    
    const schoolData = schoolDoc.data();
    logger.info(`School record found: ${JSON.stringify(schoolData)}`);
    
    // Check if school is approved
    if (!schoolData.isApproved) {
      logger.warn(`School not approved: ${uid}`);
      await firebaseSignOut(auth);
      return {
        success: false,
        error: 'Your school account is pending approval. Please contact SeekTRU support.'
      };
    }
    
    // Successfully authenticated as a school
    logger.info(`Login successful for school: ${schoolData.name || email}`);
    return {
      success: true,
      user: userCredential.user,
      isAdmin: false
    };
  } catch (error: any) {
    let errorMessage = 'An error occurred during login. Please try again.';
    
    // Handle specific Firebase auth errors
    if (error.code === 'auth/invalid-email') {
      errorMessage = 'Invalid email address format.';
    } else if (error.code === 'auth/user-disabled') {
      errorMessage = 'This account has been disabled. Please contact support.';
    } else if (error.code === 'auth/user-not-found') {
      errorMessage = 'No account found with this email address.';
    } else if (error.code === 'auth/wrong-password') {
      errorMessage = 'Incorrect password. Please try again.';
    } else if (error.code === 'auth/too-many-requests') {
      errorMessage = 'Too many unsuccessful login attempts. Please try again later.';
    }
    
    logger.error('Login error:', error);
    
    return {
      success: false,
      error: errorMessage
    };
  }
};

// Send password reset email
export const resetPassword = async (email: string): Promise<AuthResponse> => {
  try {
    await sendPasswordResetEmail(auth, email);
    logger.info(`Password reset email sent to: ${email}`);
    return {
      success: true
    };
  } catch (error: any) {
    let errorMessage = 'Failed to send password reset email. Please try again.';
    
    if (error.code === 'auth/invalid-email') {
      errorMessage = 'Invalid email address format.';
    } else if (error.code === 'auth/user-not-found') {
      errorMessage = 'No account found with this email address.';
    }
    
    logger.error('Password reset error:', error);
    return {
      success: false,
      error: errorMessage
    };
  }
};

// Sign out
export const signOut = async (): Promise<AuthResponse> => {
  try {
    await firebaseSignOut(auth);
    logger.info('User signed out successfully');
    return {
      success: true
    };
  } catch (error) {
    logger.error('Sign out error:', error);
    return {
      success: false,
      error: 'Failed to sign out. Please try again.'
    };
  }
};

// Get current user
export const getCurrentUser = (): User | null => {
  return auth.currentUser;
};

// Auth state change listener setup
export const onAuthStateChanged = (callback: (user: User | null) => void) => {
  return auth.onAuthStateChanged(callback);
};